import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Trash2, Ban, RotateCcw } from 'lucide-react';
import type { Job } from '@atlas/shared';
import { StatusBadge } from './StatusBadge.js';
import { SourceBadge } from './SourceBadge.js';
import { ProgressBar } from './ProgressBar.js';
import { JobDetailModal } from './JobDetailModal.js';
import { useAuth } from '../auth/AuthProvider.js';
import { deleteJob, cancelJob, retryJob } from '../lib/callables.js';

interface JobsTableProps {
  jobs: Array<Job & { id: string }>;
  loading?: boolean;
  emptyMessage?: string;
  onDeleted?: () => void;
  hideProgress?: boolean;
  /** Show a "Deleted by" column (used by the Deleted log view). */
  showDeletedBy?: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Rough human-readable ETA, e.g. "2h 14m", "8m", "<1m". */
function formatRemaining(seconds: number | null): string {
  if (seconds == null || !isFinite(seconds) || seconds <= 0) return '…';
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 1) return '<1m';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function DeleteModal({ job, onClose, onDeleted }: { job: Job & { id: string }; onClose: () => void; onDeleted: () => void }) {
  const [input, setInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmed = input.trim() === job.clientName;

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await deleteJob({ jobId: job.id });
      onDeleted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[400px] max-w-[95vw] p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h2 className="text-sm font-semibold text-[#37352f]">Delete import</h2>
          <p className="text-xs text-[#73726e] mt-1">
            This permanently deletes the data in S3. The job record is kept as a
            log and will show as <span className="font-medium">Deleted</span>.
            This cannot be undone.
          </p>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-[#73726e]">
            Type <span className="font-mono font-semibold text-[#37352f]">{job.clientName}</span> to confirm
          </span>
          <input
            autoFocus
            className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#e03e3e] text-[#37352f]"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && confirmed) handleDelete(); }}
          />
        </label>
        {error && <p className="text-xs text-[#e03e3e]">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-[#73726e] hover:text-[#37352f]">Cancel</button>
          {confirmed && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#e03e3e] rounded hover:bg-[#c93232] disabled:opacity-50 transition-colors"
            >
              <Trash2 size={13} />
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RowMenu({
  canCancel,
  canRetry,
  canDelete,
  busy,
  onCancel,
  onRetry,
  onDelete,
}: {
  canCancel: boolean;
  canRetry: boolean;
  canDelete: boolean;
  busy: boolean;
  onCancel: () => void;
  onRetry: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.target as Node;
      if (
        btnRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation();
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  }

  if (!canCancel && !canRetry && !canDelete) return null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        disabled={busy}
        className="p-1 rounded text-[#aeaaa4] hover:text-[#37352f] hover:bg-[#f1f1ef] transition-colors disabled:opacity-40"
      >
        <MoreHorizontal size={14} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999 }}
          className="bg-white border border-[#e9e9e7] rounded-lg shadow-lg w-36 py-1"
        >
          {canRetry && (
            <button
              onClick={() => { setOpen(false); onRetry(); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-[#37352f] hover:bg-[#f1f1ef] transition-colors"
            >
              <RotateCcw size={12} />
              Retry
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => { setOpen(false); onCancel(); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-[#d9730d] hover:bg-[#fef3e5] transition-colors"
            >
              <Ban size={12} />
              Cancel
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => { setOpen(false); onDelete(); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-[#e03e3e] hover:bg-[#fbe8e8] transition-colors"
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

export function JobsTable({
  jobs,
  loading = false,
  emptyMessage = 'No imports yet.',
  onDeleted,
  hideProgress = false,
  showDeletedBy = false,
}: JobsTableProps) {
  const { user, userRecord } = useAuth();
  const [deleteTarget, setDeleteTarget] = useState<(Job & { id: string }) | null>(null);
  const [detailTarget, setDetailTarget] = useState<(Job & { id: string }) | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Per-job throughput samples (bytes/sec, EMA-smoothed) used to estimate ETA.
  // The jobs list is polled roughly every 5s; each poll is a sample.
  const samplesRef = useRef<Map<string, { bytes: number; time: number; bps: number }>>(new Map());
  const [etas, setEtas] = useState<Record<string, number | null>>({});

  useEffect(() => {
    const now = Date.now();
    const samples = samplesRef.current;
    const next: Record<string, number | null> = {};
    const liveIds = new Set<string>();

    for (const job of jobs) {
      if (job.status !== 'running' || job.bytesTotal == null || job.bytesTotal <= 0) {
        samples.delete(job.id);
        continue;
      }
      liveIds.add(job.id);
      const bytes = job.bytesTransferred;
      const prev = samples.get(job.id);

      if (!prev) {
        samples.set(job.id, { bytes, time: now, bps: 0 });
      } else if (bytes > prev.bytes) {
        const dt = (now - prev.time) / 1000;
        const inst = dt > 0 ? (bytes - prev.bytes) / dt : prev.bps;
        const bps = prev.bps > 0 ? prev.bps * 0.5 + inst * 0.5 : inst;
        samples.set(job.id, { bytes, time: now, bps });
      }
      // else: bytes unchanged — keep the previous sample/speed.

      const s = samples.get(job.id)!;
      next[job.id] = s.bps > 0 ? (job.bytesTotal - bytes) / s.bps : null;
    }

    for (const id of [...samples.keys()]) {
      if (!liveIds.has(id)) samples.delete(id);
    }

    setEtas(next);
  }, [jobs]);

  async function handleCancel(jobId: string) {
    setBusyId(jobId);
    setActionError(null);
    try {
      await cancelJob({ jobId });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Cancel failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRetry(jobId: string) {
    setBusyId(jobId);
    setActionError(null);
    try {
      await retryJob({ jobId });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Retry failed.');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-[#aeaaa4]">
        Loading…
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-[#aeaaa4]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
    {deleteTarget && (
      <DeleteModal
        job={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); onDeleted?.(); }}
      />
    )}
    {detailTarget && (
      <JobDetailModal
        job={detailTarget}
        onClose={() => setDetailTarget(null)}
      />
    )}
    {actionError && (
      <div className="mx-3 mt-3 px-3 py-2 bg-[#fbe8e8] border border-[#f3c9c9] rounded text-xs text-[#e03e3e] flex items-center justify-between gap-2">
        <span>{actionError}</span>
        <button onClick={() => setActionError(null)} className="text-[#c93232] hover:text-[#a02828] shrink-0">Dismiss</button>
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e9e9e7]">
            {[
              'Name',
              'System',
              'Imported by',
              ...(showDeletedBy ? ['Deleted by'] : []),
              'Started',
              'Status',
              ...(!hideProgress ? ['Progress', 'Remaining'] : []),
              'S3 location',
              '',
            ].map((h, i) => (
              <th
                key={i}
                className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const isRunning = job.status === 'running';
            const isExtracting = job.status === 'extracting';
            const isDeleting = job.status === 'deleting';
            const indeterminate =
              isRunning && job.bytesTotal == null && job.bytesTransferred === 0;
            // Mutations are restricted to the job owner or an admin — others
            // get a read-only view. Backend callables enforce this too.
            const canManage =
              !!userRecord?.isAdmin || job.createdBy?.uid === user?.uid;

            return (
              <tr
                key={job.id}
                onClick={() => setDetailTarget(job)}
                className="border-b border-[#f1f1ef] hover:bg-[#fafafa] transition-colors cursor-pointer"
              >
                <td className="py-2.5 px-3 font-medium text-[#37352f] whitespace-nowrap">
                  {job.clientName}
                </td>
                <td className="py-2.5 px-3 whitespace-nowrap">
                  <SourceBadge system={job.sourceSystem} />
                </td>
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                  {job.createdBy?.displayName ?? '—'}
                </td>
                {showDeletedBy && (
                  <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                    {job.deletedBy?.displayName ? (
                      <span className="flex flex-col leading-tight">
                        <span>{job.deletedBy.displayName}</span>
                        {job.deletedAt && (
                          <span className="text-xs text-[#aeaaa4]">
                            {formatDate(job.deletedAt)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-[#aeaaa4]">—</span>
                    )}
                  </td>
                )}
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap text-xs">
                  {formatDate(job.createdAt)}
                </td>
                <td className="py-2.5 px-3 whitespace-nowrap">
                  <StatusBadge status={job.status} error={job.error} />
                </td>
                {!hideProgress && (
                  <>
                    <td className="py-2.5 px-3">
                      {isRunning ? (
                        <ProgressBar
                          percent={job.percentComplete}
                          indeterminate={indeterminate}
                          bytesTransferred={job.bytesTransferred}
                          bytesTotal={job.bytesTotal}
                        />
                      ) : isExtracting ? (
                        <ProgressBar percent={0} indeterminate />
                      ) : isDeleting ? (
                        <div className="flex flex-col gap-1 max-w-[260px]">
                          <ProgressBar percent={0} indeterminate />
                          {typeof job.deletedObjectCount === 'number' &&
                            job.deletedObjectCount > 0 && (
                              <span className="text-xs text-[#73726e] tabular-nums">
                                {job.deletedObjectCount.toLocaleString()} objects removed
                              </span>
                            )}
                        </div>
                      ) : job.status === 'deleted' ? (
                        <span className="text-xs text-[#73726e] tabular-nums">
                          {typeof job.deletedObjectCount === 'number'
                            ? `${job.deletedObjectCount.toLocaleString()} objects removed`
                            : 'Data removed'}
                        </span>
                      ) : (job.status === 'failed' ||
                          job.status === 'cancelled' ||
                          job.status === 'delete_failed') && job.error ? (
                        <span title={job.error} className="block text-xs text-[#e03e3e] max-w-[260px] truncate cursor-help">
                          {job.error}
                        </span>
                      ) : (
                        <span className="text-xs text-[#aeaaa4]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap text-xs tabular-nums">
                      {isRunning ? formatRemaining(etas[job.id] ?? null) : '—'}
                    </td>
                  </>
                )}
                <td className="py-2.5 px-3 text-[#aeaaa4] text-xs font-mono whitespace-nowrap max-w-[200px] truncate">
                  {(job.status === 'complete' ||
                    job.status === 'running' ||
                    job.status === 'extracting' ||
                    job.status === 'deleting') &&
                  job.s3Destination ? (
                    <span>
                      s3://{job.s3Destination.bucket}/{job.s3Destination.prefix}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-2.5 px-2" onClick={(e) => e.stopPropagation()}>
                  <RowMenu
                    canCancel={canManage && (job.status === 'running' || job.status === 'extracting' || job.status === 'queued')}
                    canRetry={canManage && (job.status === 'failed' || job.status === 'cancelled')}
                    canDelete={
                      canManage &&
                      job.status !== 'running' &&
                      job.status !== 'extracting' &&
                      job.status !== 'deleting' &&
                      job.status !== 'deleted'
                    }
                    busy={busyId === job.id}
                    onCancel={() => handleCancel(job.id)}
                    onRetry={() => handleRetry(job.id)}
                    onDelete={() => setDeleteTarget(job)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}
